import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import Footer from '../components/Footer'
import {
  Target,
  Trophy,
  Lightbulb,
  Sparkles,
  Users,
  Zap,
  Briefcase,
  MapPin,
  Handshake,
  Crown,
  Gamepad2,
  BarChart3,
  Mail,
} from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import config from '../services/config'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45 },
  }),
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
}

const AboutUs: React.FC = () => {
  const { t } = useTranslation()

  const missionCards = [
    { icon: Target, titleKey: 'about.teamBuilding' as const, descKey: 'about.teamBuildingDesc' as const, color: 'text-primary' },
    { icon: Trophy, titleKey: 'about.competition' as const, descKey: 'about.competitionDesc' as const, color: 'text-success' },
    { icon: Lightbulb, titleKey: 'about.innovation' as const, descKey: 'about.innovationDesc' as const, color: 'text-amber-400' },
  ]

  const values = [
    { icon: Sparkles, textKey: 'about.value1' as const },
    { icon: Users, textKey: 'about.value2' as const },
    { icon: Zap, textKey: 'about.value3' as const },
    { icon: Briefcase, textKey: 'about.value4' as const },
  ]

  const featurePills = [
    { icon: Crown, labelKey: 'about.chessFeature' as const },
    { icon: Gamepad2, labelKey: 'about.predictionsFeature' as const },
    { icon: Users, labelKey: 'about.roomsFeature' as const },
    { icon: BarChart3, labelKey: 'about.rankingsFeature' as const },
  ]

  const teamCards = [
    { icon: Trophy, titleKey: 'about.teamCard1Title' as const, taglineKey: 'about.teamCard1Tagline' as const, iconBg: 'bg-primary/15', iconColor: 'text-primary' },
    { icon: MapPin, titleKey: 'about.teamCard2Title' as const, taglineKey: 'about.teamCard2Tagline' as const, iconBg: 'bg-success/15', iconColor: 'text-success' },
    { icon: Handshake, titleKey: 'about.teamCard3Title' as const, taglineKey: 'about.teamCard3Tagline' as const, iconBg: 'bg-chess/15', iconColor: 'text-chess' },
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="pt-14 sm:pt-16 pb-10 sm:pb-12 relative gradient-bg-subtle overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-40 h-40 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-60 h-60 rounded-full bg-chess/10 blur-3xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 bg-primary/15 border border-primary/20 mb-4"
          >
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary">{t('about.title')}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-display text-display-1 font-bold text-foreground mb-3"
          >
            {t('about.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-body-lg text-muted-foreground max-w-xl mx-auto mb-3"
          >
            {t('about.tagline')}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="inline-flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground"
          >
            <MapPin className="w-4 h-4 text-primary" />
            {t('about.basedIn')}
          </motion.p>
        </div>
      </section>

      {/* Features strip - icons */}
      <section className="py-6 sm:py-8 border-b border-border/50">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display text-xl font-bold text-center text-foreground mb-5 sm:mb-6"
          >
            {t('about.featuresTitle')}
          </motion.h2>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-4 sm:gap-6"
          >
            {featurePills.map((item, i) => (
              <motion.div
                key={item.labelKey}
                variants={fadeUp}
                custom={i}
                whileHover={{ scale: 1.05, y: -2 }}
                className="flex items-center gap-3 rounded-2xl px-5 py-3 bg-muted/50 border border-border/50 hover:border-primary/30 hover:bg-muted/80 transition-colors"
              >
                <item.icon className="w-6 h-6 text-primary shrink-0" />
                <span className="font-medium text-foreground">{t(item.labelKey)}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Our Team - short taglines, big icons */}
      <section className="py-10 sm:py-14">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-display text-display-1 font-bold text-center text-foreground mb-8 sm:mb-10"
          >
            {t('about.ourTeam')}
          </motion.h2>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto"
          >
            {teamCards.map((card, i) => (
              <motion.div
                key={card.titleKey}
                custom={i}
                variants={fadeUp}
                whileHover={{ y: -4 }}
                className="group glass-card rounded-2xl p-6 sm:p-8 flex flex-col items-center text-center border border-border/50 hover:border-primary/30 transition-all duration-300"
              >
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${card.iconBg} group-hover:scale-110 transition-transform duration-300`}>
                  <card.icon className={`w-8 h-8 ${card.iconColor}`} size={32} />
                </div>
                <h3 className="font-display text-lg font-bold text-foreground mb-2">
                  {t(card.titleKey)}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-snug">
                  {t(card.taglineKey)}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Our Mission - icon-first, short text */}
      <section className="py-16 sm:py-20 gradient-bg-subtle">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-display text-display-1 font-bold text-center text-foreground mb-12"
          >
            {t('about.ourMission')}
          </motion.h2>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-8 max-w-4xl mx-auto"
          >
            {missionCards.map((card, i) => (
              <motion.div
                key={card.titleKey}
                custom={i}
                variants={fadeUp}
                whileHover={{ scale: 1.02 }}
                className="glass-card rounded-2xl p-6 sm:p-8 text-center border border-border/50 hover:border-primary/20 transition-all"
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <card.icon className={`${card.color} w-7 h-7`} size={28} />
                </div>
                <h3 className="font-display text-lg font-bold text-foreground mb-2">{t(card.titleKey)}</h3>
                <p className="text-sm text-muted-foreground leading-snug">{t(card.descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What We Value - compact icon row */}
      <section className="py-16 sm:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-display text-display-1 font-bold text-center text-foreground mb-12"
          >
            {t('about.whatWeValue')}
          </motion.h2>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto"
          >
            {values.map((v, i) => (
              <motion.div
                key={v.textKey}
                custom={i}
                variants={fadeUp}
                className="flex items-center gap-4 rounded-xl px-5 py-4 glass-card border border-border/50 hover:border-primary/20"
              >
                <v.icon className="text-primary flex-shrink-0 w-6 h-6" size={24} />
                <p className="text-sm text-muted-foreground leading-snug">{t(v.textKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Get in Touch */}
      <section className="py-16 sm:py-20 gradient-bg-subtle">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="glass-card rounded-2xl p-8 sm:p-10 lg:p-12 max-w-2xl mx-auto border border-border/50"
          >
            <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-5">
              <Mail className="w-7 h-7 text-primary" />
            </div>
            <h3 className="font-display text-display-2 font-bold text-foreground mb-3">
              {t('about.getInTouch')}
            </h3>
            <p className="text-body-lg text-muted-foreground mb-6 leading-relaxed">
              {t('about.getInTouchDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/company-registration"
                className="inline-flex justify-center items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-nav font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
              >
                {t('about.registerCompany')}
              </Link>
              <a
                href={`mailto:${config.contactEmail}`}
                className="inline-flex justify-center items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-nav font-semibold bg-muted/20 border border-border text-foreground hover:bg-muted/30 transition-colors"
              >
                <Mail className="w-4 h-4" />
                {config.contactEmail}
              </a>
              <a
                href={config.socialMedias.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex justify-center items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-xl text-nav font-semibold bg-muted/20 border border-border text-foreground hover:bg-muted/30 transition-colors"
              >
                {t('footer.instagram')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer showLinks />
      <div className="container mx-auto px-4 sm:px-6 text-center text-sm text-muted-foreground pb-6">
        <p className="inline-flex items-center gap-1.5 mt-2">
          <MapPin className="w-4 h-4" />
          {t('about.basedIn')}
        </p>
      </div>
    </div>
  )
}

export default AboutUs
